/**
 * Webhook event types for the Webhook Endpoints and Webhook Delivery Log pages
 * (fi-30).
 *
 * Both pages offer the same vocabulary: WebhookEventType, which
 * tenant-webhook-config.openapi.yaml and webhook-delivery-log.openapi.yaml
 * each reference from the PledgeWebhookEventType enum of
 * collateral-pledge.openapi.yaml. The options, and their order, are read from
 * the generated schema document of tenant-webhook-config, so a spec change
 * reaches both pages through the type cascade and never through a copy of the
 * list here.
 *
 * The labels are keyed by the generated type, so tsc fails when the enum gains
 * or loses a member until this map follows. pledge.created reads "Pledge
 * submitted", as the Notifications inbox labels pledge_created
 * (pages/Notifications.tsx EVENT_LABEL).
 *
 * Both pages load with the portal (routes/index.tsx imports them statically),
 * so an unreadable document yields no options rather than an exception that
 * would stop every page from rendering. The suite in
 * utils/__tests__/webhook-event-types.test.ts fails on that document before a
 * commit can carry it.
 */

import type { components } from '@aaimplatform/institutional-types/tenant-webhook-config';
import tenantWebhookConfigDocument from '@aaimplatform/institutional-types/generated/tenant-webhook-config.schemas.json';

export type WebhookEventType = components['schemas']['WebhookEventType'];

export const WEBHOOK_EVENT_TYPE_LABELS: Readonly<Record<WebhookEventType, string>> = {
  'pledge.created': 'Pledge submitted',
  'pledge.withdrawn': 'Pledge withdrawn',
  'pledge.expired': 'Pledge expired',
  'pledge.approaching_expiry': 'Pledge approaching expiry',
  'pledge.accepted': 'Pledge accepted',
  'pledge.declined': 'Pledge declined',
  'pledge.returned': 'Pledge returned',
  'pledge.released': 'Pledge released',
  'collateral.value_change': 'Collateral value change',
  'collateral.health_warning': 'Collateral health warning',
  'collateral.cha_tier_change': 'Collateral health tier change',
};

function isLabeledEventType(value: unknown): value is WebhookEventType {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(WEBHOOK_EVENT_TYPE_LABELS, value);
}

/** The WebhookEventType enum the generated schema document declares, in spec order. */
export function readWebhookEventTypes(document: unknown): readonly WebhookEventType[] {
  // Annotated inline: the container shape of a generated file, not an API
  // contract.
  const values = (document as { components?: { schemas?: { WebhookEventType?: { enum?: unknown } } } } | null)
    ?.components?.schemas?.WebhookEventType?.enum;
  return Array.isArray(values) ? values.filter(isLabeledEventType) : [];
}

/** WebhookEventType, in the order the spec declares it. */
export const WEBHOOK_EVENT_TYPES: readonly WebhookEventType[] = readWebhookEventTypes(tenantWebhookConfigDocument);

/** True when the value is one of WEBHOOK_EVENT_TYPES. */
export function isWebhookEventType(value: unknown): value is WebhookEventType {
  return isLabeledEventType(value) && WEBHOOK_EVENT_TYPES.includes(value);
}

/** The label of an event type, or the value itself when it is not one. */
export function webhookEventTypeLabel(value: string): string {
  return isWebhookEventType(value) ? WEBHOOK_EVENT_TYPE_LABELS[value] : value;
}
